import React from 'react';
import { GroupedCheckboxDropdown, ActionDropdown } from 'components/common';
import PropTypes from 'prop-types';
import {
  MdSearch, MdClose, MdChevronLeft, MdChevronRight, MdZoomIn, MdZoomOut,
  MdFitScreen, MdUnfoldMore, MdUnfoldLess, MdDownload,
} from 'react-icons/md';
const iconButtonClass =
  'tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 hover:tw-text-primary-600 tw-transition-colors';

const textButtonClass =
  'tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-slate-50 hover:tw-text-primary-600 tw-transition-colors';

/**
 * Search, filters and viewport controls.
 *
 * Search is fuzzy and diacritic-insensitive, and reports `3/17` with prev/next
 * so a term matching many people stays navigable instead of just lighting up
 * cards somewhere off screen.
 */
const OrgChartToolbar = ({
  query, onQueryChange, matchCount, matchIndex, onPrevMatch, onNextMatch,
  siteGroups, departmentGroups, filters, onFiltersChange,
  employmentStatus, onEmploymentStatusChange, loading,
  onZoomIn, onZoomOut, onFit, onExpandAll, onCollapseAll,
  onExportPng, onExportPdf, exporting, searchRef,
}) => {
  const hasQuery = query.trim().length > 0;

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-3">
      {/* ── Search ───────────────────────────────────────────────────────── */}
      <div className="tw-relative tw-flex tw-items-center">
        <MdSearch size={15} className="tw-absolute tw-left-2.5 tw-text-slate-400 tw-pointer-events-none" />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Name, title, email, HR code…"
          className="tw-h-8 tw-pl-8 tw-pr-8 tw-w-64 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 focus:tw-border-primary-400 focus:tw-outline-none"
        />
        {hasQuery && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            title="Clear search"
            className="tw-absolute tw-right-1.5 tw-text-slate-400 hover:tw-text-slate-700 tw-bg-transparent tw-border-0 tw-p-0.5"
          >
            <MdClose size={14} />
          </button>
        )}
      </div>

      {hasQuery && (
        <div className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-slate-500">
          <span className={matchCount === 0 ? 'tw-text-red-500' : ''}>
            {matchCount === 0 ? 'No match' : `${matchIndex + 1}/${matchCount}`}
          </span>
          <button type="button" onClick={onPrevMatch} disabled={matchCount === 0} title="Previous match" className={iconButtonClass}>
            <MdChevronLeft size={16} />
          </button>
          <button type="button" onClick={onNextMatch} disabled={matchCount === 0} title="Next match" className={iconButtonClass}>
            <MdChevronRight size={16} />
          </button>
        </div>
      )}

      <span className="tw-w-px tw-h-6 tw-bg-slate-200" />

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <GroupedCheckboxDropdown
        groups={siteGroups}
        selectedIds={filters.siteIds}
        onChange={(siteIds) => onFiltersChange({ ...filters, siteIds })}
        placeholder="All sites"
        width={220}
      />
      <GroupedCheckboxDropdown
        groups={departmentGroups}
        selectedIds={filters.departmentIds}
        onChange={(departmentIds) => onFiltersChange({ ...filters, departmentIds })}
        placeholder="All departments"
        width={260}
      />
      {/* Employment status refetches — deactivated people are not in the payload
          at all — so it is kept visually distinct from the client-side filters
          beside it, and shows a spinner hint while the new set loads. */}
      <select
        value={employmentStatus}
        onChange={(event) => onEmploymentStatusChange(event.target.value)}
        disabled={loading}
        title="Deactivated employees are fetched separately"
        className="tw-h-8 tw-px-2 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-primary-200 tw-bg-primary-50 tw-text-primary-700 tw-font-medium focus:tw-outline-none"
      >
        <option value="active">Active only</option>
        <option value="inactive">Deactivated only</option>
        <option value="all">Active + deactivated</option>
      </select>

      <select
        value={filters.status}
        onChange={(event) => onFiltersChange({ ...filters, status: event.target.value })}
        className="tw-h-8 tw-px-2 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-text-slate-600 focus:tw-outline-none"
      >
        <option value="">Everyone</option>
        <option value="sales">Sales team</option>
        <option value="bod">Board of directors</option>
      </select>

      {/* ── Viewport ─────────────────────────────────────────────────────── */}
      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5">
        <button type="button" onClick={onExpandAll} className={textButtonClass} title="Expand every branch">
          <MdUnfoldMore size={14} /> Expand all
        </button>
        <button type="button" onClick={onCollapseAll} className={textButtonClass} title="Collapse to the top level">
          <MdUnfoldLess size={14} /> Collapse all
        </button>

        <span className="tw-w-px tw-h-6 tw-bg-slate-200" />

        <button type="button" onClick={onZoomOut} className={iconButtonClass} title="Zoom out (-)"><MdZoomOut size={16} /></button>
        <button type="button" onClick={onZoomIn} className={iconButtonClass} title="Zoom in (+)"><MdZoomIn size={16} /></button>
        <button type="button" onClick={onFit} className={iconButtonClass} title="Fit to screen (0)"><MdFitScreen size={16} /></button>

        <ActionDropdown
          align="end"
          menuWidth={180}
          disabled={exporting}
          unstyledTrigger
          triggerClassName={textButtonClass}
          trigger={<><MdDownload size={14} /> {exporting ? 'Exporting…' : 'Export'}</>}
          items={[
            { label: 'Download PNG', onClick: onExportPng },
            { label: 'Download PDF', onClick: onExportPdf },
          ]}
        />
      </div>
    </div>
  );
};

OrgChartToolbar.propTypes = {
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  matchCount: PropTypes.number.isRequired,
  matchIndex: PropTypes.number.isRequired,
  onPrevMatch: PropTypes.func.isRequired,
  onNextMatch: PropTypes.func.isRequired,
  siteGroups: PropTypes.array.isRequired,
  departmentGroups: PropTypes.array.isRequired,
  filters: PropTypes.shape({
    siteIds: PropTypes.array,
    departmentIds: PropTypes.array,
    status: PropTypes.string,
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  employmentStatus: PropTypes.oneOf(['active', 'inactive', 'all']).isRequired,
  onEmploymentStatusChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onFit: PropTypes.func.isRequired,
  onExpandAll: PropTypes.func.isRequired,
  onCollapseAll: PropTypes.func.isRequired,
  onExportPng: PropTypes.func.isRequired,
  onExportPdf: PropTypes.func.isRequired,
  exporting: PropTypes.bool,
  searchRef: PropTypes.object,
};

export default OrgChartToolbar;
